import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { C } from "./theme";

const bob = (d = 0, amp = 3) => ({ animate: { y: [0, -amp, 0] }, transition: { duration: 0.5, repeat: Infinity, ease: "easeInOut", delay: d } });

const ANCHOR = [206, 20];

function Kite() {
  const [ax, ay] = ANCHOR;
  return (
    <svg viewBox="0 0 240 195" className="absolute inset-0 w-full h-full" aria-hidden overflow="visible" data-testid="taxi-kite">
      <motion.g animate={{ rotate: [-5, 4, -5] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: `${ax}px ${ay}px` }}>
        <path d={`M${ax} ${ay} Q${ax + 70} ${ay - 40} ${ax + 92} ${ay - 150}`} stroke="#6B4F52" strokeWidth="1.1" fill="none" />
        <g transform={`translate(${ax + 92} ${ay - 150}) rotate(18) scale(1.35)`}>
          <path d="M0 -56 L28 -24 L0 10 L-28 -24 Z" fill="#D6336C" />
          <path d="M0 -56 L28 -24 L0 -24 Z" fill="#F28C28" />
          <path d="M-28 -24 L0 -24 L0 10 Z" fill="#F7C325" />
          <line x1="0" y1="-56" x2="0" y2="10" stroke="#7A1E3A" strokeWidth="1.2" />
          <path d="M-28 -24 Q0 -34 28 -24" stroke="#7A1E3A" strokeWidth="1.2" fill="none" />
          <motion.path d="M0 10 q-6 8 0 14 q6 6 0 14" stroke="#D6336C" strokeWidth="1.8" fill="none" animate={{ rotate: [-10, 10, -10] }} transition={{ duration: 0.6, repeat: Infinity }} style={{ transformOrigin: "0px 10px" }} />
        </g>
      </motion.g>
      <circle cx={ax} cy={ay} r="2.2" fill="#6B4F52" />
    </svg>
  );
}

function Skyline() {
  return (
    <svg viewBox="0 0 1600 260" preserveAspectRatio="xMidYMax slice" className="absolute inset-x-0 bottom-[36%] w-full h-[34vh]" aria-hidden>
      <g fill={C.blush} opacity="0.9">
        <rect x="0" y="170" width="90" height="90" /><rect x="96" y="140" width="60" height="120" /><rect x="160" y="190" width="80" height="70" />
        <path d="M300 260 L300 120 L314 120 L314 86 L324 86 L324 40 L328 10 L332 40 L332 86 L342 86 L342 120 L356 120 L356 260 Z" />
        <rect x="370" y="160" width="70" height="100" /><rect x="446" y="200" width="60" height="60" />
        <path d="M560 260 L560 170 L580 150 L580 120 Q600 70 620 120 L620 150 L640 170 L640 260 Z" />
        <path d="M720 260 L720 140 Q720 110 760 104 Q800 110 800 140 L800 260 L784 260 L784 176 Q760 140 736 176 L736 260 Z" />
        <rect x="712" y="120" width="96" height="10" /><circle cx="726" cy="110" r="8" /><circle cx="794" cy="110" r="8" />
        <rect x="830" y="180" width="70" height="80" /><rect x="906" y="150" width="50" height="110" />
        <path d="M1000 260 L1000 110 L1012 110 L1014 70 L1018 30 L1022 70 L1024 110 L1036 110 L1036 260 Z" />
        <path d="M1080 260 L1080 190 Q1130 120 1180 190 L1180 260 Z" /><rect x="1126" y="128" width="8" height="18" />
        <rect x="1210" y="160" width="80" height="100" /><rect x="1296" y="130" width="60" height="130" /><rect x="1362" y="190" width="90" height="70" /><rect x="1460" y="150" width="70" height="110" /><rect x="1536" y="200" width="64" height="60" />
      </g>
    </svg>
  );
}

export function TaxiIntro() {
  const [phase, setPhase] = useState("drive");
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("fade"), 4400);
    const t2 = setTimeout(() => setPhase("done"), 5300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  if (phase === "done") return null;
  const vw = window.innerWidth;
  const groupW = vw < 768 ? 260 + 24 + 150 + 40 : 420 + 44 + 240 + 60;
  return (
    <motion.div
      data-testid="taxi-opening"
      className="fixed inset-0 z-[100] overflow-hidden pointer-events-none"
      style={{ background: C.cream }}
      animate={{ opacity: phase === "fade" ? 0 : 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
    >
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: -40, opacity: 1 }} transition={{ duration: 4.6, ease: "linear" }} className="absolute inset-0" data-testid="taxi-skyline">
        <Skyline />
      </motion.div>
      <div className="absolute left-0 right-0 top-[64%] h-px" style={{ background: `${C.burgundy}40` }} />
      <div className="absolute left-0 right-0 top-[64%] mt-6 h-px opacity-60" style={{ backgroundImage: `repeating-linear-gradient(to right, ${C.gold} 0 28px, transparent 28px 60px)` }} />
      <div className="absolute inset-x-0 bottom-[36%] h-0">
        <motion.div
          initial={{ x: vw + 20 }}
          animate={{ x: -groupW - 80 }}
          transition={{ duration: 4.4, ease: [0.25, 0.75, 0.75, 0.25] }}
          className="absolute bottom-0 left-0 flex items-end"
          data-testid="taxi-cab"
        >
          <motion.div className="relative w-[260px] md:w-[420px] shrink-0" {...bob(0, 1.5)}>
            <img src="/intro/taxi.webp" alt="" className="w-full h-auto block" draggable={false} />
          </motion.div>
          <motion.div className="relative w-[150px] md:w-[240px] shrink-0 ml-[24px] md:ml-[44px]" data-testid="taxi-rickshaw" {...bob(0.15, 2)}>
            <img src="/intro/auto.webp" alt="" className="w-full h-auto block" draggable={false} />
            <Kite />
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
}
